import { useEffect, useState } from "react";
import { collection, query, onSnapshot } from "firebase/firestore";
import { db } from "../firebase/config";           // your Firebase config
import { useAuth } from "../context/AuthContext"; // your auth hook

// ── TYPES ────────────────────────────────────────────────────────────────────

type Status =
  | "submitted"
  | "under_review"
  | "acknowledged"
  | "evaluation"
  | "approved"
  | "rejected"
  | "issued"
  | "completed";

interface ApplicationCounts {
  total:    number;
  pending:  number;
  approved: number;
  rejected: number;
  loading:  boolean;
  error:    string | null;
}

// ── STATUS GROUPS ─────────────────────────────────────────────────────────────

const PENDING_STATUSES:  Status[] = ["submitted", "under_review", "acknowledged", "evaluation"];
const APPROVED_STATUSES: Status[] = ["approved", "issued", "completed"];
const REJECTED_STATUSES: Status[] = ["rejected"];

// ── HOOK ──────────────────────────────────────────────────────────────────────

export function useApplicationCounts(): ApplicationCounts {
  const { user } = useAuth();

  const [counts, setCounts] = useState<ApplicationCounts>({
    total:    0,
    pending:  0,
    approved: 0,
    rejected: 0,
    loading:  true,
    error:    null,
  });

  useEffect(() => {
    // Don't run if user is not logged in
    if (!user) return;

    // Single query — all applications belonging to this user
    // One read is more efficient than four separate queries
    const q = query(
      collection(db, "applications")
    );

    // onSnapshot → updates counts in real-time whenever Firestore changes
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        let pending  = 0;
        let approved = 0;
        let rejected = 0;

        snapshot.forEach((doc) => {
          const status = doc.data().status as Status;

          if (PENDING_STATUSES.includes(status))       pending++;
          else if (APPROVED_STATUSES.includes(status)) approved++;
          else if (REJECTED_STATUSES.includes(status)) rejected++;
        });

        setCounts({
          total:    snapshot.size, // total documents returned
          pending,
          approved,
          rejected,
          loading:  false,
          error:    null,
        });
      },
      (error) => {
        // Firestore error (e.g. missing index, permission denied)
        console.error("useApplicationCounts error:", error);
        setCounts((prev) => ({
          ...prev,
          loading: false,
          error:   error.message,
        }));
      }
    );

    // Cleanup listener when component unmounts
    return () => unsubscribe();

  }, [user]); // re-runs if the logged-in user changes

  return counts;
}
